import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// UI Library imports
import { Container, Header, Grid } from '../ui/layout';
import { Button, Select, SearchInput } from '../ui/form';
import { Card, Badge, StatCard, StatNumber, StatLabel, EmptyState, SkeletonTableRow, SkeletonTableCell, SkeletonText, SkeletonTextShort, SkeletonBadge } from '../ui/data-display';
import { Pagination, PaginationButton, PaginationInfo } from '../ui/navigation';
import { Title, Text, ClickableText } from '../ui/common';


// Common components
import BulkURLImport from '../common/BulkURLImport';
import BulkDataUpdate from '../common/BulkDataUpdate';

// Modal action components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e1e8ed;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

interface Contact {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role?: string;
  location?: string;
  company_id?: string | {
    _id: string;
    name: string;
    industry?: string;
  };
  company?: Company; // Populated company data from backend aggregation
  sales_rep_id?: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  sales_rep?: User; // Populated sales rep data from backend aggregation
  last_activity_at?: string;
  status_outcome?: 'nuovo' | 'positivo' | 'negativo' | 'in_corso';
  notes?: string;
  created_at: string;
  updated_at: string;
  isActive?: boolean;
}

interface Company {
  _id: string;
  name: string;
  industry?: string;
  headquarters?: string;
  website?: string;
  created_at?: string;
  updated_at?: string;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  role?: string;
}

// Specialized styled components that don't have direct UI library equivalents
const CreateButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`;

const FiltersContainer = styled(Grid)`
  grid-template-columns: 2fr 1fr 1fr;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StatsContainer = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  margin-bottom: 2rem;
`;

const FilterSelect = styled(Select)`
  border: 2px solid #e1e8ed;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #667eea;
  }
`;

// Table Headers
const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 1fr) minmax(100px, 1fr) minmax(180px, 2fr) minmax(130px, 1.5fr) minmax(130px, 1.5fr) minmax(100px, 1fr) minmax(180px, 2fr) minmax(130px, 1.5fr) minmax(80px, 1fr);
  gap: 0.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  
  > div {
    text-align: left;
  }
`;

const ContactsList = styled.div`
  background: white;
  overflow: hidden;
  --skeleton-line-height: 26px;
  --table-grid-columns: minmax(100px, 1fr) minmax(100px, 1fr) minmax(180px, 2fr) minmax(130px, 1.5fr) minmax(130px, 1.5fr) minmax(100px, 1fr) minmax(180px, 2fr) minmax(130px, 1.5fr) minmax(80px, 1fr);
  --table-grid-gap: 0.5rem;
  --table-row-padding: 1rem;
  --table-row-border: 1px solid #e9ecef;
  --table-row-background: white;
`;

const ContactCard = styled(Card)`
  display: grid;
  grid-template-columns: minmax(100px, 1fr) minmax(100px, 1fr) minmax(180px, 2fr) minmax(130px, 1.5fr) minmax(130px, 1.5fr) minmax(100px, 1fr) minmax(180px, 2fr) minmax(130px, 1.5fr) minmax(80px, 1fr);
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  border-radius: 0;
  box-shadow: none;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background: #f8f9fa;
    transform: none;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ContactDetail = styled(Text)`
  color: #64748b;
  font-size: 0.875rem;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  
  &:hover::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 0;
    background: #1f2937;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    white-space: normal;
    z-index: 1000;
    max-width: 300px;
    word-wrap: break-word;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    display: ${props => props.title && props.title.length > 20 ? 'block' : 'none'};
  }
  
  &:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 10px;
    margin-bottom: -5px;
    border: 5px solid transparent;
    border-top-color: #1f2937;
    z-index: 1001;
    display: ${props => props.title && props.title.length > 20 ? 'block' : 'none'};
  }
`;

// Status Badge - using specialized variant from UI library
const StatusBadge = styled(Badge)<{ status: string }>`
  background: ${props => {
    switch (props.status) {
      case 'nuovo': return '#f3f4f6';
      case 'positivo': return '#dcfce7';
      case 'negativo': return '#fee2e2';
      case 'in_corso': return '#fef3c7';
      case 'qualified': return '#dcfce7';
      case 'contacted': return '#dbeafe';
      case 'interested': return '#fef3c7';
      case 'not_interested': return '#fee2e2';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'nuovo': return '#374151';
      case 'positivo': return '#166534';
      case 'negativo': return '#dc2626';
      case 'in_corso': return '#92400e';
      case 'qualified': return '#166534';
      case 'contacted': return '#1e40af';
      case 'interested': return '#92400e';
      case 'not_interested': return '#dc2626';
      default: return '#475569';
    }
  }};
`;

const getStatusLabel = (status: string | undefined): string => {
  switch (status) {
    case 'nuovo': return 'Nuovo';
    case 'positivo': return 'Positivo';
    case 'negativo': return 'Negativo';
    case 'in_corso': return 'In Corso';
    default: return 'Nuovo';
  }
};

const ContactsManagement: React.FC = () => {
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to get initial state from URL
  const getInitialParam = (key: string, defaultVal: string = '') => {
    const params = new URLSearchParams(location.search);
    return params.get(key) || defaultVal;
  };

  // Check permissions
  const canAddContact = hasRole(['admin', 'super_user']);
  const canImportData = hasRole(['admin']);
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => getInitialParam('search'));
  const [statusOutcomeFilter, setStatusOutcomeFilter] = useState(() => getInitialParam('status_outcome'));
  const [companySearchTerm, setCompanySearchTerm] = useState(() => getInitialParam('companySearch'));
  const [salesRepSearchTerm, setSalesRepSearchTerm] = useState(() => getInitialParam('salesRepSearch'));
  const [sortMode, setSortMode] = useState<'alpha' | 'updated'>(() => {
    const val = getInitialParam('sort');
    return (val === 'alpha' || val === 'updated') ? val : 'alpha';
  });
  const [stats, setStats] = useState({
    total: 0,
    nuovo: 0,
    positivo: 0,
    negativo: 0,
    in_corso: 0
  });
  const [currentPage, setCurrentPage] = useState(() => {
    const page = parseInt(getInitialParam('page', '1'), 10);
    return isNaN(page) || page < 1 ? 1 : page;
  });
  const [totalPages, setTotalPages] = useState(1);
  const pendingScrollY = useRef<number | null>(null);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (statusOutcomeFilter) params.set('status_outcome', statusOutcomeFilter);
    if (companySearchTerm) params.set('companySearch', companySearchTerm);
    if (salesRepSearchTerm) params.set('salesRepSearch', salesRepSearchTerm);
    if (sortMode !== 'alpha') params.set('sort', sortMode);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    navigate(`?${params.toString()}`, { replace: true });
  }, [searchTerm, statusOutcomeFilter, companySearchTerm, salesRepSearchTerm, sortMode, currentPage, navigate]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const pageSize = 10;
  const expectedRowCount = stats.total > 0
    ? Math.min(pageSize, Math.max(stats.total - (currentPage - 1) * pageSize, 0))
    : 0;
  const skeletonRowCount = expectedRowCount > 0
    ? expectedRowCount
    : (contacts && contacts.length > 0 ? contacts.length : pageSize);

  // Check if user can manage contacts (admin/super_user can manage all, users can only manage assigned contacts)
  const canManageAllContacts = hasRole(['admin', 'super_user']);
  console.log('ContactsManagement - canManageAllContacts:', canManageAllContacts);
  console.log('ContactsManagement - user role:', user?.role);

  // Restore scroll position from sessionStorage (when returning from detail page)
  useEffect(() => {
    const saved = sessionStorage.getItem('contactsScrollY');
    if (saved) {
      pendingScrollY.current = parseInt(saved, 10);
      sessionStorage.removeItem('contactsScrollY');
    }
  }, []);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [currentPage, searchTerm, statusOutcomeFilter, companySearchTerm, salesRepSearchTerm, sortMode]);

  useEffect(() => {
    if (!loading && pendingScrollY.current !== null) {
      window.scrollTo({ top: pendingScrollY.current, behavior: 'auto' });
      pendingScrollY.current = null;
    }
  }, [loading]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(statusOutcomeFilter && { status_outcome: statusOutcomeFilter }),
        ...(companySearchTerm && { companySearch: companySearchTerm }),
        ...(salesRepSearchTerm && { salesRepSearch: salesRepSearchTerm }),
        sort: sortMode
      });

      const response = await axios.get(`/api/contacts?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Fix: Access contacts from the nested data structure
      setContacts(response.data.data?.contacts || []);
      setTotalPages(response.data.data?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(statusOutcomeFilter && { status_outcome: statusOutcomeFilter }),
        ...(companySearchTerm && { companySearch: companySearchTerm }),
        ...(salesRepSearchTerm && { salesRepSearch: salesRepSearchTerm })
      });

      const response = await axios.get(`/api/contacts/stats?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setStats(response.data.data || {
        total: 0,
        nuovo: 0,
        positivo: 0,
        negativo: 0,
        in_corso: 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getCompanyName = (contact: Contact): string => {
    if (contact.company && typeof contact.company === 'object') {
      return contact.company.name;
    }
    if (contact.company_id && typeof contact.company_id === 'object') {
      return contact.company_id.name;
    }
    return 'N/A';
  };

  const getCompanyId = (contact: Contact): string | null => {
    if (contact.company && typeof contact.company === 'object') {
      return contact.company._id;
    }
    if (contact.company_id && typeof contact.company_id === 'object') {
      return contact.company_id._id;
    }
    if (typeof contact.company_id === 'string') {
      return contact.company_id;
    }
    return null;
  };

  const getSalesRepName = (contact: Contact): string => {
    if (contact.sales_rep && typeof contact.sales_rep === 'object') {
      return `${contact.sales_rep.firstName} ${contact.sales_rep.lastName}`;
    }
    if (contact.sales_rep_id && typeof contact.sales_rep_id === 'object') {
      return `${contact.sales_rep_id.firstName} ${contact.sales_rep_id.lastName}`;
    }
    return 'N/A';
  };

  const getSalesRepId = (contact: Contact): string | null => {
    if (contact.sales_rep && typeof contact.sales_rep === 'object') {
      return contact.sales_rep._id;
    }
    if (contact.sales_rep_id && typeof contact.sales_rep_id === 'object') {
      return contact.sales_rep_id._id;
    }
    if (typeof contact.sales_rep_id === 'string') {
      return contact.sales_rep_id;
    }
    return null;
  };

  // Save scroll position before navigating to detail/other pages
  const navigateWithScroll = (path: string) => {
    sessionStorage.setItem('contactsScrollY', String(window.scrollY));
    navigate(path);
  };

  const handleCreateContact = () => {
    navigateWithScroll('/dashboard/contacts/new');
  };

  const handlePageChange = (nextPage: number) => {
    pendingScrollY.current = window.scrollY;
    setCurrentPage(nextPage);
  };

  const handleURLImportSuccess = () => {
    // Refresh the contacts list and close modal
    fetchContacts();
    fetchStats();
    setShowCreateModal(false);
  };

  const handleModalClose = () => {
    if (!isProcessing) {
      setShowCreateModal(false);
    }
  };



  return (
    <Container>
      <Header>
        <Title>Gestione Contatti</Title>
        <div style={{ display: 'flex', gap: '12px' }}>
          {canAddContact && (
            <CreateButton onClick={handleCreateContact}>
              Aggiungi Contatto
            </CreateButton>
          )}
          {canImportData && (
            <CreateButton onClick={() => setShowCreateModal(true)}>
              Importa LinkedIn
            </CreateButton>
          )}
          {canImportData && (
            <CreateButton onClick={() => setShowBulkUpdateModal(true)}>
              Aggiungi Dati
            </CreateButton>
          )}
        </div>
      </Header>

      {/* Filters */}
      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Cerca contatti..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <FilterSelect
          value={statusOutcomeFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setStatusOutcomeFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="">Tutti gli Stati</option>
          <option value="nuovo">Nuovo</option>
          <option value="positivo">Positivo</option>
          <option value="negativo">Negativo</option>
          <option value="in_corso">In Corso</option>
        </FilterSelect>
        <SearchInput
          type="text"
          placeholder="Cerca per azienda..."
          value={companySearchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setCompanySearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <SearchInput
          type="text"
          placeholder="Cerca per commerciale..."
          value={salesRepSearchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSalesRepSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
          <Button
            variant={sortMode === 'alpha' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => { setSortMode('alpha'); setCurrentPage(1); }}
            title="Alfabetico"
          >
            A→Z
          </Button>
          <Button
            variant={sortMode === 'updated' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => { setSortMode('updated'); setCurrentPage(1); }}
            title="Ultimo Aggiornamento"
          >
            Ultimo Aggiornamento
          </Button>
        </div>
      </FiltersContainer>

      {/* Stats */}
      <StatsContainer>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Totale</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.nuovo}</StatNumber>
          <StatLabel>Nuovo</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.positivo}</StatNumber>
          <StatLabel>Positivo</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.negativo}</StatNumber>
          <StatLabel>Negativo</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.in_corso}</StatNumber>
          <StatLabel>In Corso</StatLabel>
        </StatCard>
      </StatsContainer>

      <TableContainer>
        <TableHeader>
          <div>Cognome</div>
          <div>Nome</div>
          <div>Azienda</div>
          <div>Sede</div>
          <div>Ruolo</div>
          <div>Cell</div>
          <div>Email</div>
          <div>Commerciale</div>
          <div>Status</div>
        </TableHeader>
        <ContactsList>
          {loading ? (
            Array.from({ length: skeletonRowCount }).map((_, index) => (
              <SkeletonTableRow key={index}>
                <SkeletonTableCell>
                  <SkeletonTextShort />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonTextShort />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonText />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonTextShort />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonText />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonText />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonText />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonTextShort />
                </SkeletonTableCell>
                <SkeletonTableCell>
                  <SkeletonBadge />
                </SkeletonTableCell>
              </SkeletonTableRow>
            ))
          ) : (
            contacts && contacts.map(contact => (
              <ContactCard
                key={contact._id}
                onClick={() => navigateWithScroll(`/dashboard/contacts/${contact._id}`)}
              >
              <ContactDetail title={contact.last_name}>
                  {contact.last_name}
                </ContactDetail>
                <ContactDetail title={contact.first_name}>
                  {contact.first_name}
                </ContactDetail>
                <ContactDetail title={getCompanyName(contact)}>
                  <ClickableText 
                    onClick={(e) => {
                      e.stopPropagation();
                      const companyId = getCompanyId(contact);
                      if (companyId) {
                        navigateWithScroll(`/dashboard/companies/${companyId}`);
                      }
                    }}
                  >
                    {getCompanyName(contact)}
                  </ClickableText>
                </ContactDetail>
                <ContactDetail title={contact.location || 'N/A'}>
                  {contact.location || 'N/A'}
                </ContactDetail>
                <ContactDetail title={contact.role || 'N/A'}>
                  {contact.role || 'N/A'}
                </ContactDetail>
                <ContactDetail title={contact.phone || 'N/A'}>
                  {contact.phone || 'N/A'}
                </ContactDetail>
                <ContactDetail title={contact.email}>
                  <a 
                    href={`mailto:${contact.email}`} 
                    style={{ color: '#667eea', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {contact.email}
                  </a>
                </ContactDetail>
                <ContactDetail title={getSalesRepName(contact)}>
                  <ClickableText 
                    onClick={(e) => {
                      e.stopPropagation();
                      const salesRepId = getSalesRepId(contact);
                      if (salesRepId) {
                        navigateWithScroll(`/dashboard/users/${salesRepId}`);
                      }
                    }}
                  >
                    {getSalesRepName(contact)}
                  </ClickableText>
                </ContactDetail>
                <ContactDetail>
                  <StatusBadge status={contact.status_outcome || 'nuovo'}>
                    {getStatusLabel(contact.status_outcome)}
                  </StatusBadge>
                </ContactDetail>
              </ContactCard>
            ))
          )}
        </ContactsList>
      </TableContainer>

      {!loading && (!contacts || contacts.length === 0) && (
        <EmptyState>
          <Title>Nessun contatto trovato</Title>
          <Text>
            {searchTerm || statusOutcomeFilter || companySearchTerm || salesRepSearchTerm
              ? 'Prova a modificare i criteri di ricerca'
              : 'Inizia creando il tuo primo contatto'}
          </Text>
        </EmptyState>
      )}

      <Pagination>
        <PaginationButton
          disabled={loading || currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Precedente
        </PaginationButton>
        <PaginationInfo>
          Pagina {currentPage} di {totalPages} ({stats.total} contatti totali)
        </PaginationInfo>
        <PaginationButton
          disabled={loading || currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Successivo
        </PaginationButton>
      </Pagination>

      {/* Create Contact Modal */}
      <ModalOverlay 
        onClick={handleModalClose} 
        style={{ display: showCreateModal ? 'flex' : 'none' }}
      >
        <ModalContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Crea Nuovo Contatto</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <BulkURLImport
              onSuccess={handleURLImportSuccess}
              onCancel={handleModalClose}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
            />
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>

      {/* Bulk Update Modal */}
      <ModalOverlay 
        onClick={() => setShowBulkUpdateModal(false)} 
        style={{ display: showBulkUpdateModal ? 'flex' : 'none' }}
      >
        <ModalContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Aggiornamento Massivo Contatti</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <BulkDataUpdate
              onSuccess={() => {
                fetchContacts();
                fetchStats();
                setShowBulkUpdateModal(false);
              }}
              onCancel={() => setShowBulkUpdateModal(false)}
            />
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>
    </Container>
  );
};

export default ContactsManagement;
